/**
 * Build-time env table.
 *
 * The renderer bundle is built with `target: ['web', 'electron-renderer']`
 * and `nodeIntegration: false`, which means the `process` global does NOT
 * exist at runtime. Webpack's EnvironmentPlugin only replaces *static*
 * `process.env.KEY` references at compile time — dynamic accesses like
 * `process.env[key]` are left untouched and would produce a
 * "ReferenceError: process is not defined" in the packaged renderer.
 *
 * We therefore build a literal object using one static reference per key.
 * After webpack runs, every `process.env.X` below becomes the inlined
 * string literal from the build-time `.env`, and the runtime code never
 * touches the `process` global at all.
 */
const BUILD_TIME_ENV: Record<string, string | undefined> = {
  NODE_ENV: process.env.NODE_ENV,
  APP_ENV: process.env.APP_ENV,
  SENTRY_DSN: process.env.SENTRY_DSN,
  // PowerSync
  POWERSYNC_URL: process.env.POWERSYNC_URL,
  // Supabase
  SUPABASE_URL: process.env.SUPABASE_URL,
  SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
  SUPABASE_PUBLISHABLE_API_KEY: process.env.SUPABASE_PUBLISHABLE_API_KEY,
  SUPABASE_PUBLISHABLE_API_KEY_PROD:
    process.env.SUPABASE_PUBLISHABLE_API_KEY_PROD,
  // Stripe — monthly
  STRIPE_STARTER_PRICE_ID: process.env.STRIPE_STARTER_PRICE_ID,
  STRIPE_STARTER_PRICE_ID_PROD: process.env.STRIPE_STARTER_PRICE_ID_PROD,
  STRIPE_PROFESSIONAL_PRICE_ID: process.env.STRIPE_PROFESSIONAL_PRICE_ID,
  STRIPE_PROFESSIONAL_PRICE_ID_PROD:
    process.env.STRIPE_PROFESSIONAL_PRICE_ID_PROD,
  STRIPE_ENTERPRISE_PRICE_ID: process.env.STRIPE_ENTERPRISE_PRICE_ID,
  STRIPE_ENTERPRISE_PRICE_ID_PROD: process.env.STRIPE_ENTERPRISE_PRICE_ID_PROD,
  // Stripe — annual
  STRIPE_STARTER_ANNUAL_PRICE_ID: process.env.STRIPE_STARTER_ANNUAL_PRICE_ID,
  STRIPE_STARTER_ANNUAL_PRICE_ID_PROD:
    process.env.STRIPE_STARTER_ANNUAL_PRICE_ID_PROD,
  STRIPE_PROFESSIONAL_ANNUAL_PRICE_ID:
    process.env.STRIPE_PROFESSIONAL_ANNUAL_PRICE_ID,
  STRIPE_PROFESSIONAL_ANNUAL_PRICE_ID_PROD:
    process.env.STRIPE_PROFESSIONAL_ANNUAL_PRICE_ID_PROD,
  STRIPE_ENTERPRISE_ANNUAL_PRICE_ID:
    process.env.STRIPE_ENTERPRISE_ANNUAL_PRICE_ID,
  STRIPE_ENTERPRISE_ANNUAL_PRICE_ID_PROD:
    process.env.STRIPE_ENTERPRISE_ANNUAL_PRICE_ID_PROD,
  // Google OAuth
  GOOGLE_CLIENT_ID_DESKTOP: process.env.GOOGLE_CLIENT_ID_DESKTOP,
  GOOGLE_CLIENT_SECRET_DESKTOP: process.env.GOOGLE_CLIENT_SECRET_DESKTOP,
  GOOGLE_CLIENT_ID_WEB: process.env.GOOGLE_CLIENT_ID_WEB,
  GOOGLE_CLIENT_SECRET_WEB: process.env.GOOGLE_CLIENT_SECRET_WEB,
};

export const isElectron = (): boolean =>
  typeof window !== 'undefined' && !!window.electron?.ipcRenderer;

export const canSignUp = !isElectron(); // && getEnvVar('NODE_ENV')

export const getEnvVar = (key: string): string | undefined => {
  // Prefer the runtime IPC bridge (populated from the main process .env
  // loader) so values can change without rebuilding the renderer.
  const fromIpc = (window as any)?.electron?.ipcRenderer?.getEnv?.(key);
  if (fromIpc) return fromIpc;
  // Fall back to values that webpack inlined at build time. Empty strings
  // are treated as "not set" so the call site's || fallback chain works.
  const fromBuild = BUILD_TIME_ENV[key];
  return fromBuild || undefined;
};

export const formatDate = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
};

export const formatTime = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '--:--';
  return parsedDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const getFirstName = (value?: string | null): string => {
  if (!value) return '';
  const [first] = value.trim().split(/\s+/);
  return first || '';
};

// function useAutoLockTimeout(currentUser: any) {
//   const dispatch = useAppDispatch();
//   const navigate = useNavigate();
//   const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

//   useEffect(() => {
//     if (currentUser) {
//       const resetTimer = () => {
//         if (timeoutRef.current) {
//           clearTimeout(timeoutRef.current);
//         }
//         timeoutRef.current = setTimeout(
//           async () => {
//             try {
//               await dispatch(logout()).unwrap();
//               navigate('/');
//               apiError('Logged out due to inactivity.');
//             } catch (err) {
//               console.warn(
//                 '[AutoLock] Session logout skipped due to unsynced local data:',
//                 err,
//               );
//             }
//           },
//           2 * 60 * 1000,
//         ); // 2 minutes
//       };

//       const events = [
//         'mousemove',
//         'mousedown',
//         'keydown',
//         'touchstart',
//         'scroll',
//         'click',
//       ];

//       resetTimer();

//       events.forEach((evt) => {
//         window.addEventListener(evt, resetTimer);
//       });

//       return () => {
//         if (timeoutRef.current) {
//           clearTimeout(timeoutRef.current);
//         }
//         events.forEach((evt) => {
//           window.removeEventListener(evt, resetTimer);
//         });
//       };
//     }

//     if (timeoutRef.current) {
//       clearTimeout(timeoutRef.current);
//       timeoutRef.current = null;
//     }
//     return undefined;
//   }, [currentUser, dispatch, navigate]);
// }
